<template>
  <Transition name="confetti-fade">
    <div v-if="isActive" class="confetti-overlay">
      <div v-if="activeLabel || activeIcon" class="confetti-label">
        <span v-if="activeEmoji" class="confetti-emoji">{{ activeEmoji }}</span>
        <GamIcon v-else :name="confettiIcon" size="28px" is-primary />
        <span>{{ confettiLabel }}</span>
      </div>
      <div v-for="i in PARTICLE_COUNT" :key="i" class="confetti-piece" :style="getParticleStyle(i)" />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { useConfetti } from '@/views/composables/hooks/useConfetti';
import { computed } from 'vue';

const { isActive, activeIcon, activeEmoji, activeLabel } = useConfetti();

const confettiIcon = computed(() => (activeIcon.value as GamIconName) || GamIconName.HANDSHAKE);
const confettiLabel = computed(() => activeLabel.value || localize('handshake.confetti.label'));

const PARTICLE_COUNT = 60;
const COLORS = ['#e84393', '#fd79a8', '#ffffff', '#ffeaa7', '#55efc4', '#74b9ff', '#a29bfe'];

function getParticleStyle(index: number) {
  const left = Math.random() * 100;
  const delay = Math.random() * 1.5;
  const duration = 2 + Math.random() * 2;
  const size = 4 + Math.random() * 6;
  const color = COLORS[index % COLORS.length];
  const rotation = Math.random() * 360;
  const drift = -30 + Math.random() * 60;

  return {
    left: `${left}%`,
    width: `${size}px`,
    height: `${size * (0.4 + Math.random() * 0.6)}px`,
    backgroundColor: color,
    animationDelay: `${delay}s`,
    animationDuration: `${duration}s`,
    '--drift': `${drift}px`,
    '--rotation': `${rotation}deg`,
  };
}
</script>

<style lang="scss" scoped>
@keyframes confetti-fall {
  0% {
    transform: translateY(-10vh) translateX(0) rotate(0deg);
    opacity: 1;
  }

  80% {
    opacity: 1;
  }

  100% {
    transform: translateY(110vh) translateX(var(--drift)) rotate(calc(var(--rotation) + 720deg));
    opacity: 0;
  }
}

.confetti-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 99999;
  pointer-events: none;
  overflow: hidden;
}

.confetti-emoji {
  font-size: 14px;
  line-height: 1;
}

.confetti-label {
  position: absolute;
  top: 30%;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border-radius: 999px;
  background: var(--color-dark-70);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid var(--color-pink-30);
  box-shadow: 0 0 32px var(--color-pink-30);
  color: var(--color-white-100);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.05em;
  white-space: nowrap;
  z-index: 2;
  animation: confetti-label-pop 0.4s ease-out;
}

@keyframes confetti-label-pop {
  0% {
    opacity: 0;
    transform: translateX(-50%) scale(0.7);
  }

  100% {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }
}

.confetti-piece {
  position: absolute;
  top: -10px;
  border-radius: 2px;
  animation: confetti-fall linear forwards;
}

.confetti-fade-enter-active {
  transition: opacity 0.2s ease;
}

.confetti-fade-leave-active {
  transition: opacity 0.5s ease;
}

.confetti-fade-enter-from,
.confetti-fade-leave-to {
  opacity: 0;
}
</style>
